import React, { useMemo } from 'react';
import { ApexChart, GoogleMapComponent, TextToolTip } from 'components';
import { Svg } from '../../../../assets';
import moment from 'moment';

export const FmvReport = ({ reportFmv }) => {

  const options = {
    chart: {
      type: 'area',
      height: 400,
      toolbar: { show: false },
      animations: { enabled: false },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    dataLabels: { enabled: false },
    series: [{
      name: 'Series 1',
      data: Array.isArray(reportFmv?.valueHistory)
        ? [null, ...reportFmv.valueHistory.map((item) => item?.value)]
        : [],
    }],
    fill: {
      opacityFrom: 0.1,
      opacityTo: 0.1,
    },
    xaxis: {
      categories: ['', ...(reportFmv?.valueHistory ?? []).map((item) => `${item.quarter} ${item.year}`)],

      labels: {
        rotate: -45,
        style: {
          fontSize: '12px',
          fontFamily: 'Inter',
          colors: '#535862',
        },
      },
    },
    yaxis: {
      opposite: true,
      labels: {
        formatter: function(val) {
          return val
            ? Number(val).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            : '';
        },
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          colors: '#6b7280',
        },
      },
    },
    grid: {
      borderColor: '#e4e7ec',
      strokeDashArray: 0,
      padding: {
        left: 10,
        right: 10,
      },
    },
    tooltip: {
      y: {
        formatter: function(val) {
          return val
            ? Number(val).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            : '';
        },
      },
    },
  };

  const mapOptions = {
    mapTypeId: 'roadmap',
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    zoomControl: true,
    styles: [
      { featureType: 'road.local', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
      { featureType: 'poi.school', stylers: [{ visibility: 'on' }] },
      { featureType: 'poi.business', stylers: [{ visibility: 'on' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#f0f0f0' }, { weight: 1.5 }] },
      { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#a0d8f3' }] },
    ],
  };


  const properties = useMemo(() => {
    const otherComp = reportFmv?.comparable
      .filter(item =>
        !isNaN(parseFloat(item.latitude)) &&
        !isNaN(parseFloat(item.longitude)),
      )
      .map((item, index) => ({
        position: {
          lat: parseFloat(item.latitude),
          lng: parseFloat(item.longitude),
        },
        title: `${item.streetAddress}, ${item.city}, ${item.state} ${item.zipcode}`,
        label: String(index + 1),
        color: '#ef4444',
        isSubject: false,
      }));

    return [
      {
        position: {
          lat: parseFloat(reportFmv.latitude),
          lng: parseFloat(reportFmv.longitude),
        },
        title: 'Subject Property',
        label: 'S',
        color: '#0b5dcf',
        isSubject: true,
      },

      ...otherComp,
    ];
  }, [reportFmv?.comparable]);


  const yearRange = (items) => {
    if (!items || items.length === 0) return '';

    const years = items.map((i) => i.year);
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);

    return `${minYear}-${maxYear}`;
  };

  return (
    <div className='main-fmv'>
      <div className='main-container'>
        <section className='header-section'>
          <header className='header'>
            <div className='header-logo-block-wrapper'>
              <div className='header-logo-block'>
                <img className='logo' src='https://claimfrv-prod.s3.us-west-1.amazonaws.com/Logo.png' alt='logo' />
              </div>
            </div>


            <div className='header-info'>
              <h1 className='report-title'>FAIR MARKET VALUE (FMV) REPORT</h1>
              <p className='date-info'>Date Created: {moment(reportFmv?.createdAt).format('MM/DD/YYYY')}</p>
              {reportFmv?.claimNumber && <p className='claim-info'>Claim #: {reportFmv?.claimNumber}</p>}
            </div>
          </header>

          <div className='property-overview'>
            <div className='property-card'>
              <div className='property-content'>
                <h2 className='property-title'>Estimated Fair Market Value</h2>
                <div className='property-details'>
                  <p className='price-section'>
                    {reportFmv?.estimated}
                  </p>

                  <p className='disclaimer'>
                    This is an estimate based on comparable sales and current market trends.
                    It does not represent a formal appraisal.
                  </p>
                </div>
              </div>

              <div className='separator'></div>

              <div className='property-stats'>
                <div className='stat-item'>
                  <TextToolTip
                    classNameText='stat-value'
                    text={reportFmv?.roomTotal ? reportFmv?.roomTotal : '-'}
                    content={reportFmv?.roomTotal ? reportFmv?.roomTotal : '-'}
                    wrapperStyles={{ width: 'fit-content' }}
                  />
                  <span className='stat-label'>Total rooms</span>
                </div>

                <div className='stat-item'>
                  <TextToolTip
                    classNameText='stat-value'
                    text={reportFmv?.bedrooms ? reportFmv?.bedrooms : '-'}
                    content={reportFmv?.bedrooms ? reportFmv?.bedrooms : '-'}
                    wrapperStyles={{ width: 'fit-content' }}
                  />
                  <span className='stat-label'>Bedrooms</span>
                </div>
                <div className='stat-item'>
                  <TextToolTip
                    classNameText='stat-value'
                    text={reportFmv?.bathrooms ? reportFmv?.bathrooms : '-'}
                    content={reportFmv?.bathrooms ? reportFmv?.bathrooms : '-'}
                    wrapperStyles={{ width: 'fit-content' }}
                  />
                  <span className='stat-label'>Bathrooms</span>
                </div>
                <div className='stat-item'>
                  <TextToolTip
                    classNameText='stat-value'
                    text={reportFmv?.livingArea ? reportFmv?.livingArea : '-'}
                    content={reportFmv?.livingArea ? reportFmv?.livingArea : '-'}
                    wrapperStyles={{ width: 'fit-content' }}
                  />
                  <span className='stat-label'>sqft</span>
                </div>
              </div>
            </div>

            <div className='property-image-wrapper'>
              {reportFmv?.imgSrc ?
                <img
                  className='property-image'
                  src={reportFmv?.imgSrc}
                  alt='main'
                />
                :
                <img
                  className='property-image-empty'
                  src={'https://claimfrv-prod.s3.us-west-1.amazonaws.com/house-2%402x.png'}
                  alt='main'
                />
              }


              <div className='image-overlay'>
                <address className='property-address'>
                  {reportFmv?.address}, {reportFmv?.city}, {reportFmv?.state} {reportFmv?.zipcode}
                </address>
              </div>
            </div>
          </div>
        </section>

        <section className='overview-section'>
          <div className='section-header'>
            <h2 className='section-title'>Overview</h2>
            <p className='section-subtitle'>{reportFmv?.address}, {reportFmv?.city}, {reportFmv?.state}</p>
          </div>

          <div className='property-details-flex'>
            <div className='detail-item'>
              <span className='detail-label'>Property Type</span>
              <TextToolTip
                text={reportFmv?.propertyType}
                content={reportFmv?.propertyType}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />
            </div>
            <div className='detail-item'>
              <span className='detail-label'>Heat/Air</span>
              <TextToolTip
                text={reportFmv?.heatAir}
                content={reportFmv?.heatAir}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />
            </div>
            <div className='detail-item'>
              <span className='detail-label'>Year Built</span>
              <TextToolTip
                text={reportFmv?.yearBuilt}
                content={reportFmv?.yearBuilt}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />
            </div>
            <div className='detail-item'>
              <span className='detail-label'>Stories</span>
              <TextToolTip
                text={reportFmv?.floorCount}
                content={reportFmv?.floorCount}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />
            </div>
            <div className='detail-item'>
              <span className='detail-label'>Lot Size</span>
              <TextToolTip
                text={reportFmv?.lotSize ? Number(reportFmv?.lotSize).toFixed(2) : '--'}
                content={reportFmv?.lotSize ? Number(reportFmv?.lotSize).toFixed(2) : '--'}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />

            </div>
            <div className='detail-item'>
              <span className='detail-label'>County</span>

              <TextToolTip
                text={reportFmv?.county}
                content={reportFmv?.county}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />
            </div>

            <div className='detail-item'>
              <span className='detail-label'>Garages</span>
              <TextToolTip
                text={reportFmv?.garageSpaces}
                content={reportFmv?.garageSpaces}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />
            </div>
            <div className='detail-item'>
              <span className='detail-label'>Parcel #</span>
              <TextToolTip
                text={reportFmv?.parcelNumber}
                content={reportFmv?.parcelNumber}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />
            </div>
            <div className='detail-item'>
              <span className='detail-label'>Pools</span>
              <span className='detail-value'>
                   {reportFmv?.pools ? 'Yes' : 'No'}
               </span>
            </div>
            <div className='detail-item'>
              <span className='detail-label'>Assessed Value</span>
              <TextToolTip
                text={reportFmv?.lastAssessedValue}
                content={reportFmv?.lastAssessedValue}
                classNameText='detail-value'
                wrapperStyles={{ width: 'fit-content' }}
              />
            </div>
            <div className='detail-item'>
              <span className='detail-label'>Fireplace</span>
              <span className='detail-value'>
                {reportFmv?.fireplace ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </section>
        {!!reportFmv?.valueHistory?.length &&
          <section className='value-history-section'>
            <div className='section-header'>
              <h2 className='section-title'>Value History</h2>
              <p className='section-subtitle'>{yearRange(reportFmv?.valueHistory)}</p>
            </div>

            <div id='chart-container' style={{ width: '100%' }}>
              <ApexChart options={options} />
            </div>
          </section>
        }


        <section className='comparables-section'>
          <div className='section-header'>
            <h2 className='section-title'>Comparables</h2>
            <p className='section-subtitle'>Recent Sales in the Area</p>
          </div>

          <GoogleMapComponent mapOptions={mapOptions} properties={properties} />
        </section>

        <div className='value-summary'>
          <div className='summary-item'>
            <div className='summary-value'>{reportFmv?.avgSalePrice}</div>
            <div className='summary-label'>Average sale price</div>
          </div>
          <div className='summary-divider'></div>
          <div className='summary-item'>
            <div className='summary-value primary'>{reportFmv?.compEstimated}</div>
            <div className='summary-label primary'>Estimated Fair Market Value</div>
          </div>
        </div>
        {!!reportFmv?.comparable?.length &&

          <div className='table-container'>
            <table className='comparables-table'>
              <thead>
              <tr style={{ borderSpacing: '0 !important' }}>
                <th></th>
                <th>
                  <TextToolTip
                    text={'Dist. (mi.)'}
                    content={'Dist. (mi.)'}
                    classNameText='table-header'
                  />
                </th>
                <th>
                  <TextToolTip
                    text='Beds'
                    content={'Beds'}
                    classNameText='table-header'
                  />
                </th>
                <th>
                  <TextToolTip
                    text='Baths'
                    content={'Baths'}
                    classNameText='table-header'
                  />
                </th>
                <th>
                  <TextToolTip
                    text='Liv. Area (sq. ft.)'
                    content={'Liv. Area (sq. ft.)'}
                    classNameText='table-header'
                  />
                </th>
                <th>
                  <TextToolTip
                    text='Lot Size (sq. ft.)'
                    content={'Lot Size (sq. ft.)'}
                    classNameText='table-header'
                  />
                </th>
                <th>
                  <TextToolTip
                    text='Built'
                    content={'Built'}
                    classNameText='table-header'
                  />
                </th>
                <th>
                  <TextToolTip
                    text='Sale Price'
                    content={'Sale Price'}
                    classNameText='table-header'
                  />
                </th>
                <th>
                  <TextToolTip
                    text='Sale Date'
                    content={'Sale Date'}
                    classNameText='table-header'
                  />
                </th>
              </tr>
              </thead>
              <tbody>
              {reportFmv?.comparable?.map((item, i) => (
                <tr key={i}>
                  <td className='property-cell'>
                    <div className='property-info'>
                      <div className='property-info-img-block'>
                        {item.imgSrc ?
                          <img src={item.imgSrc} alt='Property' className='property-thumb' />
                          :
                          <img src={'https://claimfrv-prod.s3.us-west-1.amazonaws.com/house-2%402x.png'} alt='Property'
                               className='property-thumb-empty' />
                        }

                        <div className='pin-marker'>
                          <Svg.MapPinSvg />
                        </div>

                        <p className='pin-marker-text'>{i + 1}</p>
                      </div>
                      <div className='property-text'>
                        <TextToolTip
                          text={item.streetAddress}
                          content={item.streetAddress}
                          classNameText='property-address-text'
                        />

                        <TextToolTip
                          text={`${item.city || ''}, ${item.state || ''} ${item.zipcode || ''}`}
                          content={`${item.city || ''}, ${item.state || ''} ${item.zipcode || ''}`}
                          classNameText='property-city'
                        />
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className='table-body-cell-fmv'>
                      <TextToolTip
                        text={item.distance}
                        content={item.distance}
                        classNameText='table-body-cell-text-fmv'
                      />
                    </div>
                  </td>
                  <td>
                    <div className='table-body-cell-fmv'>
                      <TextToolTip
                        text={item.bedrooms}
                        content={item.bedrooms}
                        classNameText='table-body-cell-text-fmv'
                      />
                    </div>
                  </td>
                  <td>
                    <div className='table-body-cell-fmv'>
                      <TextToolTip
                        text={item.bathrooms}
                        content={item.bathrooms}
                        classNameText='table-body-cell-text-fmv'
                      />
                    </div>
                  </td>
                  <td>
                    <div className='table-body-cell-fmv'>
                      <TextToolTip
                        text={Number(item.livingArea).toFixed(2)}
                        content={Number(item.livingArea).toFixed(2)}
                        classNameText='table-body-cell-text-fmv'
                      />
                    </div>
                  </td>
                  <td>
                    <div className='table-body-cell-fmv'>
                      <TextToolTip
                        text={item.lotSize ? Number(item.lotSize).toFixed(2) : ''}
                        content={Number(item.lotSize).toFixed(2)}
                        classNameText='table-body-cell-text-fmv'
                      />
                    </div>
                  </td>
                  <td>
                    <div className='table-body-cell-fmv'>
                      <TextToolTip
                        text={item.built}
                        content={item.built}
                        classNameText='table-body-cell-text-fmv'
                      />
                    </div>
                  </td>
                  <td>
                    <div className='table-body-cell-fmv'>
                      <TextToolTip
                        text={item.salePrice}
                        content={item.salePrice}
                        classNameText='table-body-cell-text-fmv'
                      />
                    </div>
                  </td>
                  <td>
                    <div className='table-body-cell-fmv'>
                      <TextToolTip
                        text={item.saleDate}
                        content={item.saleDate}
                        classNameText='table-body-cell-text-fmv'
                      />
                    </div>
                  </td>
                </tr>
              ))}

              </tbody>
            </table>
          </div>
        }

        <div className='notes-section'>
          <h3 className='notes-title'>Please Note</h3>
          <div className='notes-list'>
            <div className='note-item'>
              <div className='check-icon'>
                <Svg.CheckSvg3 />
              </div>
              <p>The estimate is generated using AI algorithms and comparable sales data from recent market
                activity.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

